import React, {
  createContext,
  useCallback,
  useReducer,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useCart from "../hooks/useCart";
import usePriceCalculator from "../components/priceCalculator";
import {
  clearProductDrafts,
  deleteProductDraft,
  getProductDrafts,
  saveProductDraft,
} from "../utils/productDraftStorage";

const CartContext = createContext();

const isObjectUrl = (value) =>
  typeof value === "string" && value.startsWith("blob:");

const stripTransientDraftFields = (draft) => {
  const { previewUrl, ...persistedDraft } = draft;
  return persistedDraft;
};

const getPrimaryDraftBlob = (draft) => {
  const fileWithBlob = (draft.viewFiles ?? []).find((file) => file?.blob);
  return fileWithBlob?.blob ?? null;
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case "SetCart":
      return action.cart; // Set the initial cart state
    case "Add": {
      // Check if the motif already exists in the cart
      const existingItem = state.find((item) => item.id === action.motif.id);
      if (existingItem) {
        // Update the quantity if the motif is already in the cart
        return state.map((item) =>
          item.id === action.motif.id
            ? { ...item, quantity: action.quantity }
            : item,
        );
      }
      // If it's a new item, add it to the cart with the quantity
      return [...state, { ...action.motif, quantity: action.quantity }];
    }
    case "UpdateQuantity":
      return state.map((item) =>
        item.id === action.id ? { ...item, quantity: action.quantity } : item,
      );
    case "Remove":
      return state.filter((item) => item.id !== action.id);
    case "Clear":
      return [];
    default:
      return state;
  }
};

const CartProvider = ({ children }) => {
  const {
    cart: initialCart,
    addToCart,
    updateCart,
    removeFromCart,
    clearCart,
  } = useCart(); // Hook to interact with backend
  const [cart, dispatch] = useReducer(cartReducer, []);
  const [productDrafts, setProductDrafts] = useState([]);
  const draftPreviewUrlsRef = useRef(new Set());

  const priceCalculator = usePriceCalculator();
  const { calculatePrice, priceInfo, formatPrice, applyVAT } = priceCalculator;

  useEffect(() => {
    if (!cart?.length) return;

    for (const item of cart) {
      if (item.cartItemType === "productDraft") continue;
      if (!item?.width || !item?.height) continue;

      calculatePrice(
        item.quantity,
        item.width,
        item.height,
        item.colors,
        item.id,
        item.userId, // if you have it; otherwise pass null
        { applyAssembly: false }, // drawer preview; checkout can still add assembly separately
      );
    }
  }, [cart, calculatePrice]);

  const normalizeCart = (cart) => {
    return cart.map((item) => ({
      ...item,
      ...item.motif, // Spread motif details into the main item object
      motif: undefined, // Remove the nested motif object if necessary
    }));
  };

  const revokeDraftPreviewUrl = useCallback((url) => {
    if (!draftPreviewUrlsRef.current.has(url)) return;
    URL.revokeObjectURL(url);
    draftPreviewUrlsRef.current.delete(url);
  }, []);

  const revokeAllDraftPreviewUrls = useCallback(() => {
    draftPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    draftPreviewUrlsRef.current.clear();
  }, []);

  const hydrateProductDraft = useCallback((draft) => {
    const previewBlob = getPrimaryDraftBlob(draft);
    const previewUrl = previewBlob
      ? URL.createObjectURL(previewBlob)
      : isObjectUrl(draft.previewUrl)
        ? draft.productImage
        : draft.previewUrl || draft.productImage;

    if (previewBlob && previewUrl) {
      draftPreviewUrlsRef.current.add(previewUrl);
    }

    return {
      ...draft,
      cartItemType: "productDraft",
      previewUrl,
    };
  }, []);

  // Initialize the cart by fetching from backend whenever initialCart updates
  useEffect(() => {
    if (Array.isArray(initialCart)) {
      dispatch({ type: "SetCart", cart: normalizeCart(initialCart) });
    }
  }, [initialCart]);

  useEffect(() => {
    let mounted = true;

    getProductDrafts().then((drafts) => {
      if (!mounted) return;
      revokeAllDraftPreviewUrls();
      setProductDrafts(drafts.map(hydrateProductDraft));
    });

    return () => {
      mounted = false;
      revokeAllDraftPreviewUrls();
    };
  }, [hydrateProductDraft, revokeAllDraftPreviewUrls]);

  const draftPriceInfo = useMemo(
    () =>
      Object.fromEntries(
        productDrafts.map((draft) => [draft.id, draft.pricing ?? {}]),
      ),
    [productDrafts],
  );

  const combinedPriceInfo = useMemo(
    () => ({ ...priceInfo, ...draftPriceInfo }),
    [priceInfo, draftPriceInfo],
  );

  const combinedCart = useMemo(
    () => [
      ...cart.map((item) => ({ ...item, cartItemType: "motif" })),
      ...productDrafts,
    ],
    [cart, productDrafts],
  );

  // Add item to cart, both in frontend state and backend
  const handleAddToCart = async (motif, quantity) => {
    try {
      await addToCart(motif.id, quantity); // Backend call
      dispatch({ type: "Add", motif, quantity });
    } catch (error) {
      console.error("Failed to add item to cart", error);
    }
  };

  const handleAddProductDraft = async (draft) => {
    const nextDraft = {
      ...draft,
      cartItemType: "productDraft",
      id: draft.id || `product-draft-${Date.now()}`,
    };

    await saveProductDraft(stripTransientDraftFields(nextDraft));
    const hydratedDraft = hydrateProductDraft(nextDraft);
    setProductDrafts((prev) => [
      ...prev.filter((item) => {
        if (item.id === hydratedDraft.id) revokeDraftPreviewUrl(item.previewUrl);
        return item.id !== hydratedDraft.id;
      }),
      hydratedDraft,
    ]);
    return hydratedDraft;
  };

  const handleUpdateCartQuantity = async (id, quantity) => {
    const productDraft = productDrafts.find((item) => item.id === id);
    if (productDraft) {
      const nextDraft = {
        ...productDraft,
        quantity: Math.max(1, Number(quantity) || 1),
      };
      await saveProductDraft(stripTransientDraftFields(nextDraft));
      setProductDrafts((prev) =>
        prev.map((item) => (item.id === id ? nextDraft : item)),
      );
      return;
    }

    const normalizedQuantity = Math.max(10, Number(quantity) || 10);

    dispatch({ type: "UpdateQuantity", id, quantity: normalizedQuantity });

    try {
      await updateCart(id, normalizedQuantity);
    } catch (error) {
      console.error("Failed to update cart quantity", error);
      // Optional: re-fetch cart from backend, or revert if you store previous quantity
    }
  };

  // Remove item from cart, both in frontend state and backend

  const handleRemoveFromCart = async (id) => {
    const productDraft = productDrafts.find((item) => item.id === id);
    if (productDraft) {
      await deleteProductDraft(id);
      revokeDraftPreviewUrl(productDraft.previewUrl);
      setProductDrafts((prev) => prev.filter((item) => item.id !== id));
      return;
    }

    try {
      await removeFromCart(id); // Backend call
      dispatch({ type: "Remove", id });
    } catch (error) {
      console.error("Failed to remove item from cart", error);
    }
  };

  // Clear cart, both in frontend state and backend
  const handleClearCart = async () => {
    try {
      if (cart.length > 0) await clearCart(); // Backend call
      await clearProductDrafts();
      revokeAllDraftPreviewUrls();
      dispatch({ type: "Clear" });
      setProductDrafts([]);
    } catch (error) {
      console.error("Failed to clear cart", error);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart: combinedCart,
        motifCart: cart,
        productDrafts,
        handleAddToCart,
        handleAddProductDraft,
        handleRemoveFromCart,
        handleClearCart,
        handleUpdateCartQuantity,
        priceInfo: combinedPriceInfo,
        formatPrice,
        applyVAT,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export { CartContext, CartProvider };
